import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Typography, Box, Checkbox, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { LawListFilter } from '../CustomerLaws/CustomerLaws.List.Filter';
import { useFilter } from '../../../../../Components/ListFilter';
import { AptorApi, IGridQueryRequest, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { MTableActions, MTableToolbar, Query } from '@material-table/core';
import { makeStyles } from '@material-ui/core/styles';
import { ICustomerLaw } from '../CustomerLaws/CustomerLaws.List';
import { StepButtonBar } from '../EditLegalListWizard/StepButtonBar';
import { StepProps } from './types';
import UpdateConnectionInfo from './UpdateConnectionInfo';

const CenteredActions = (props: any) => (
  <Box style={{ width: 100, display: 'flex', justifyContent: 'center' }}><MTableActions {...props} /></Box>
);

const useConnectionToolbarStyles = makeStyles({ toolbar: { paddingLeft: '0 !important' } });
const useConnectionTableStyles = makeStyles({
  root: { '& tbody > tr > td:first-child': { paddingLeft: '0 !important', paddingRight: '0 !important' } },
});

const ConnectionToolbar = (props: any) => {
  const classes = useConnectionToolbarStyles();
  return <MTableToolbar {...props} className={`${props.className || ''} ${classes.toolbar}`} />;
};

interface Step3LawsTableProps extends StepProps {
  selectedCompanyUnit?: { id: number; name: string };
  connectionAddedToLaws: Array<{ id: number; name: string; number: string }>;
  connectionRemovedFromLaws: Array<{ id: number; name: string; number: string }>;
  lawHasConnection: (lawId: number) => boolean;
  addLawConnection: (law: { id: number; name: string; number: string }) => void;
  removeLawConnection: (law: { id: number; name: string; number: string }) => void;
  currentStepName: string;
    updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

const Step3LawsTable = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  selectedCompanyUnit,
  connectionAddedToLaws,
  connectionRemovedFromLaws,
  lawHasConnection,
  addLawConnection,
  removeLawConnection,
  currentStepName,
    updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step3LawsTableProps) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const tableClasses = useConnectionTableStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useFilter();
  const tableRef = useRef<any>(null);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const [visibleRows, setVisibleRows] = useState<ICustomerLaw[]>([]);

  useEffect(() => {
    if (isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, isFiltersInitialized]);

  useEffect(() => {
    if (selectedCompanyUnit) {
      setFilters('companyUnit', [selectedCompanyUnit.name]);
    }
    setIsFiltersInitialized(true);
  }, [selectedCompanyUnit, setFilters]);

  const handleDataFetch = useCallback(
    async (query: Query<ICustomerLaw>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      setVisibleRows([]);
      const result = await api.searchCustomerLaws<ICustomerLaw>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'number',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
      setVisibleRows(result.data || []);
      return result;
    },
    [api, filters, isFiltersInitialized]
  );

  const check = (law: ICustomerLaw) => {
    let isSelected = lawHasConnection(law.id);
    return {
      tooltip: formatMessage({
        id: isSelected ? 'component.mtable.unselectTooltip' : 'component.mtable.selectTooltip',
      }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      isFreeAction: true,
      onClick: () => {
        if (isSelected) {
          removeLawConnection(law);
        } else {
          addLawConnection(law);
        }
      },
      iconProps: {
        style: { color: '#c4122f' },
      },
    };
  };

  const hasChanges = connectionAddedToLaws.length > 0 || connectionRemovedFromLaws.length > 0;

  return (
    <div className={tableClasses.root} style={{ width: '100%' }}>
      <div style={{ width: '100%', padding: isMobile ? theme.spacing(2) : theme.spacing(3), backgroundColor: '#f7f0f1', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontSize:"16px" }}>
          {formatMessage({ id: 'law-portal.manage.law-list.update-laws.description' })}
        </Typography>
        <LawListFilter disable={true} companyUnit={selectedCompanyUnit} filters={filters} setFilter={setFilters} />
        <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
          <MTable
            tableRef={tableRef}
            title={
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box style={{ width: 76, paddingLeft: 24, boxSizing: 'border-box', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                  checked={visibleRows.length > 0 && visibleRows.every(row => lawHasConnection(row.id))}
                  indeterminate={visibleRows.some(row => lawHasConnection(row.id)) && !visibleRows.every(row => lawHasConnection(row.id))}
                  disabled={visibleRows.length === 0}
                  onChange={(_, checked) => {
                    visibleRows.forEach(row => {
                      if (lawHasConnection(row.id) !== checked) check(row).onClick();
                    });
                  }}
                  style={{ color: '#c4122f' }}
                />
                </Box>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {formatMessage({ id: 'law-portal.evalution.table.selectAll' })}
                </Typography>
              </Box>
            }
            columns={[
              { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
              {
                title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
                field: 'name',
                render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
              },
              {
                title: formatMessage({ id: 'law-portal.manage.law-list.table.description' }),
                field: 'description',
                render: (row) => <HtmlView variant="body2" value={row.description} />,
                sorting: false,
              },
              { title: formatMessage({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
              {
                title: formatMessage({ id: 'law-portal.manage.law-list.table.notes' }),
                field: 'notes',
                render: (row) => <OwnNotes {...row} linkTo={`/law-portal/our-laws/${row.id}`} />,
                sorting: false,
              },
            ]}
            actions={[check]}
            actionLabel={formatMessage({ id: 'law-portal.manage.update-laws.connection' })}
            data={handleDataFetch}
            options={{
              actionsColumnIndex: 0,
              actionsCellStyle: { width: 100, minWidth: 100, maxWidth: 100, padding: 0 },
              showTitle: true,
            }}
            components={{ Actions: CenteredActions, Toolbar: ConnectionToolbar }}
          />
        </Box>
      </div>
      {/* <UpdateConnectionInfo
      updateConnectionInfo={updateConnectionInfo}
      currentStepName={currentStepName}
      onUpdate={setUpdateConnectionInfo}
    /> */}
      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
          nextDisabled={!hasChanges}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step3LawsTable;
